import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import GlassCard from './ui/GlassCard';
import Btn from './ui/Btn';
import Field from './ui/Field';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import SearchBar from './ui/SearchBar';
import Header from './ui/Header';
import InfoTable from './ui/InfoTable';
import Icon from './ui/Icon';
import { formatDate } from '../utils/format';

interface Client {
  id: number;
  name: string;
  inn?: string;
  phone?: string;
  city?: string;
  contact?: string;
  created_at?: string;
}

interface ClientForm {
  name: string;
  inn: string;
  phone: string;
  city: string;
  contact: string;
}

interface ClientsSectionProps {
  onViewRepairs?: (clientId: number) => void;
}

type View = 'list' | 'detail' | 'form';

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
};

const AVATAR_COLORS = [
  { bg: 'rgba(45,212,191,0.12)', border: 'rgba(45,212,191,0.35)', text: 'var(--teal)' },
  { bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.35)', text: '#a78bfa' },
  { bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.35)', text: 'var(--amber)' },
  { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.35)', text: 'var(--red)' },
  { bg: 'rgba(52,211,153,0.12)', border: 'rgba(52,211,153,0.35)', text: '#34d399' },
  { bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.35)', text: '#60a5fa' },
];

const getAvatarColor = (name: string) =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const Avatar: React.FC<{ name: string; size?: number }> = ({ name, size = 42 }) => {
  const color = getAvatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color.bg,
      border: `1px solid ${color.border}`,
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
      fontFamily: 'var(--mono)',
      fontWeight: 700,
      fontSize: size * 0.33,
      color: color.text,
      letterSpacing: '0.03em',
      userSelect: 'none',
    }}>
      {getInitials(name)}
    </div>
  );
};

const ClientsSection: React.FC<ClientsSectionProps> = ({ onViewRepairs }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('list');
  const [selected, setSelected] = useState<Client | null>(null);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [form, setForm] = useState<ClientForm>({ name: '', inn: '', phone: '', city: '', contact: '' });
  const [errors, setErrors] = useState<Partial<ClientForm>>({});
  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const res = await api.getClients();
      setClients(res.data);
    } catch (err) {
      console.error('Error loading clients', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const filtered = useMemo(() =>
    clients.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.city || '').toLowerCase().includes(search.toLowerCase()) ||
      (c.phone || '').includes(search)
    ),
    [clients, search]
  );

  const openAdd = () => {
    setForm({ name: '', inn: '', phone: '', city: '', contact: '' });
    setErrors({});
    setSelected(null);
    setView('form');
  };

  const openEdit = (client: Client) => {
    setForm({
      name: client.name,
      inn: client.inn || '',
      phone: client.phone || '',
      city: client.city || '',
      contact: client.contact || '',
    });
    setErrors({});
    setSelected(client);
    setView('form');
  };

  const save = async () => {
    const err: Partial<ClientForm> = {};
    if (!form.name.trim()) err.name = 'Поле обязательно';
    if (Object.keys(err).length) { setErrors(err); return; }

    setSaving(true);
    try {
      if (selected) {
        const res = await api.updateClient(selected.id, form);
        const updated: Client = res.data;
        setClients(cs => cs.map(c => c.id === updated.id ? updated : c));
        setSelected(updated);
        setView('detail');
      } else {
        const res = await api.createClient(form);
        setClients(cs => [res.data, ...cs]);
        setView('list');
      }
    } catch (err) {
      console.error('Save error', err);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!selected) return;
    try {
      await api.deleteClient(selected.id);
      setClients(cs => cs.filter(c => c.id !== selected.id));
      setView('list');
      setConfirm(false);
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title="Клиенты" />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
          Загрузка...
        </div>
      </div>
    );
  }

  if (view === 'form') return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={selected ? 'Редактировать' : 'Новый клиент'} onBack={() => setView(selected ? 'detail' : 'list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 100px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 }}>
          <Field label="Наименование" required error={errors.name}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Компания или ФИО" />
          </Field>
          <Field label="ИНН">
            <input value={form.inn} onChange={e => setForm(f => ({ ...f, inn: e.target.value }))} placeholder="0000000000" />
          </Field>
          <Field label="Телефон">
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+7 000 000-00-00" />
          </Field>
          <Field label="Город / Адрес">
            <input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Город, улица" />
          </Field>
          <Field label="Контактное лицо">
            <input value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} placeholder="Имя, должность" />
          </Field>
        </div>
      </div>
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: 10 }}>
        <Btn variant="primary" onClick={save} icon="check" disabled={saving} style={{ flex: 1, justifyContent: 'center' }}>
          {saving ? 'Сохранение...' : 'Сохранить'}
        </Btn>
        <Btn variant="ghost" onClick={() => setView(selected ? 'detail' : 'list')}>Отмена</Btn>
      </div>
    </div>
  );

  if (view === 'detail' && selected) return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {confirm && (
        <ConfirmDialog
          message={`Удалить «${selected.name}»?`}
          onConfirm={del}
          onCancel={() => setConfirm(false)}
        />
      )}
      <Header title="Клиент" onBack={() => setView('list')} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <Avatar name={selected.name} size={58} />
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text0)', marginBottom: 4 }}>
              {selected.name}
            </h2>
            <span style={{ color: 'var(--text2)', fontSize: 13 }}>
              Добавлен {formatDate(selected.created_at)}
            </span>
          </div>
        </div>
        <InfoTable rows={[
          ['Телефон', selected.phone || '—'],
          ['Город / Адрес', selected.city || '—'],
          ['ИНН', selected.inn || '—'],
          ['Контактное лицо', selected.contact || '—'],
        ]} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
          {onViewRepairs && (
            <Btn variant="subtle" icon="repairs" onClick={() => onViewRepairs(selected.id)} style={{ justifyContent: 'center' }}>
              Ремонты клиента
            </Btn>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="ghost" icon="edit" onClick={() => openEdit(selected)} style={{ flex: 1, justifyContent: 'center' }}>
              Изменить
            </Btn>
            <Btn variant="danger" icon="trash" onClick={() => setConfirm(true)} style={{ flex: 1, justifyContent: 'center' }}>
              Удалить
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header
        title="Клиенты"
        action={
          <Btn variant="primary" icon="plus" onClick={openAdd} style={{ padding: '7px 14px' }}>
            Добавить
          </Btn>
        }
      />
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Имя, город, телефон..." />
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {filtered.length === 0 ? (
          <EmptyState icon="clients" title="Клиентов нет" sub="Добавьте первого клиента" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(c => (
              <GlassCard key={c.id} onClick={() => { setSelected(c); setView('detail'); }} style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar name={c.name} size={42} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: 'var(--text0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {c.name}
                    </div>
                    <div style={{ display: 'flex', gap: 10, color: 'var(--text2)', fontSize: 13 }}>
                      {c.city && <span>{c.city}</span>}
                      {c.phone && <span style={{ fontFamily: 'var(--mono)' }}>{c.phone}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <Icon name="chevron" size={15} color="var(--text3)" />
                    <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                      {formatDate(c.created_at)}
                    </span>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsSection;
